import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import Document from '@/components/common/Document';
import ErrorMessage from '@/components/common/ErrorMessage';
import ViewDocumentModal from '@/components/common/ViewDocumentModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import Button from '@/components/ui/Button';
import { IDeleteResponse, INewDocuments } from '@/interfaces/common.model';
import { IDocument } from '@/interfaces/user.model';
import { EmployeeDocs } from '@/utility/enum';
import { showToastNotification } from '@/utility/helper';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
interface IEmployeeDocumentsStep8Props {
  existingDocuments: IDocument[];
  setExistingDocuments: React.Dispatch<React.SetStateAction<IDocument[]>>;
  empId: string | null;
  nextStep: () => void;
}
const EmployeeDocumentsStep8 = ({
  existingDocuments,
  setExistingDocuments,
  empId,
  nextStep,
}: IEmployeeDocumentsStep8Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newDocuments, setNewDocuments] = useState<INewDocuments[]>([]);
  const [documentError, setDocumentError] = useState<string>('');
  const [selectedDocument, setSelectedDocument] =
    useState<Partial<IDocument> | null>(null);
  const navigate = useNavigate();
  const [isDocumentOpen, setIsDocumentOpen] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const toggleModal = () => {
    setIsDocumentOpen(!isDocumentOpen);
  };
  const toggleConfirmModal = () => setConfirmModal;
  const handleDeleteDocument = async () => {
    try {
      const response: IDeleteResponse = await callApi(
        `${API_URL + API_ENDPOINT.deleteDocument}?documentId=${selectedId}`,
        { method: 'GET' },
      );
      toggleConfirmModal();
      if (response?.status) {
        setExistingDocuments((prev) =>
          prev.filter((doc) => doc.id !== selectedId),
        );
        showToastNotification('success', response.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };
  const handleNextStep = async () => {
    if (newDocuments?.length > 0) {
      const invalidDocuments = newDocuments?.some((doc) => {
        return Object.values(doc).includes(null);
      });
      if (invalidDocuments) {
        showToastNotification('danger', 'Please select file for Document');
        return;
      }
      const formData = new FormData();
      newDocuments.forEach((doc) => {
        const [key, file] = Object.entries(doc)[0];
        if (file) {
          formData.append(key, file);
        }
      });
      setIsLoading(true);
      try {
        const response: IDeleteResponse = await callApi(
          `${API_URL + API_ENDPOINT.addDocuments}?userId=${empId}`,
          {
            method: 'POST',
            data: formData,
            multipart: true,
          },
        );
        if (response?.status) {
          showToastNotification('success', response.message);
          nextStep();
          setNewDocuments([]);
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          showToastNotification('danger', (error as IApiErrorResponse).message);
        } else {
          showToastNotification('danger', 'Something went wrong');
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      navigate(-1);
    }
  };
  return (
    <>
      <Document
        docs={EmployeeDocs}
        newDocuments={newDocuments}
        existingDocuments={existingDocuments}
        toggleModal={toggleModal}
        toggleConfirmModal={toggleConfirmModal}
        setSelectedId={setSelectedId}
        setNewDocuments={setNewDocuments}
        setSelectedDocument={setSelectedDocument}
        setDocumentError={setDocumentError}
      />
      <ErrorMessage message={documentError} />
      <div className="d-flex align-items-center justify-content-end">
        <Button
          className="btn btn-primary px-4"
          type="button"
          onClick={handleNextStep}
          isLoading={isLoading}
        >
          Save and Continue
        </Button>
      </div>
      <ViewDocumentModal
        isOpen={isDocumentOpen}
        toggle={toggleModal}
        selectedDocument={selectedDocument}
      />
      <ConfirmModal
        isOpen={confirmModal}
        toggle={toggleConfirmModal}
        submitHandler={handleDeleteDocument}
        isLoading={isLoading}
      />
    </>
  );
};
export default EmployeeDocumentsStep8;
